import { useEffect, useRef, useCallback } from 'react';
import { User, OrderTriggerCondition } from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	UISerializableOrder,
	UIMarket,
	formatOrderSize,
	getOrderLabelFromOrderDetails,
	isOrderTriggered,
} from '@velocity-exchange/common';
import NumLib from 'src/utils/NumLib';
import { notify } from 'src/utils/notifications';
import { getAllNonZeroOrders } from 'src/hooks/query/accountTradingData/utils/computeOrders';

/**
 * Detects newly triggered orders and fires toast notifications.
 * Runs per-account, listening to userAccountUpdate events.
 */
export function useTriggeredOrderNotifications(
	userKey: string | undefined,
	user: User | undefined
) {
	const previousOrdersRef = useRef<UISerializableOrder[]>([]);
	const isFirstLoadRef = useRef(true);

	// Reset refs when switching accounts to avoid false notifications
	useEffect(() => {
		previousOrdersRef.current = [];
		isFirstLoadRef.current = true;
	}, [userKey]);

	const checkForTriggeredOrders = useCallback(
		(nonZeroOrders: UISerializableOrder[]) => {
			const currentTriggeredOrders = nonZeroOrders.filter(isOrderTriggered);

			// Skip notifications on first load - these orders were already triggered when UI started
			if (isFirstLoadRef.current) {
				isFirstLoadRef.current = false;
				previousOrdersRef.current = [...nonZeroOrders];
				return;
			}

			// Find newly triggered orders by comparing with previous state
			const previousTriggeredOrderIds = new Set(
				previousOrdersRef.current
					.filter(isOrderTriggered)
					.map((order) => order.orderId)
			);

			const newlyTriggeredOrders = currentTriggeredOrders.filter(
				(order) => !previousTriggeredOrderIds.has(order.orderId)
			);

			// Show notifications for newly triggered orders
			newlyTriggeredOrders.forEach((order) => {
				const uiMarket = new UIMarket(order.marketIndex, order.marketType);
				const marketName = uiMarket.uiSymbols.marketDisplaySymbol;

				// Convert TRIGGERED_ABOVE/BELOW to base trigger conditions since util doesn't handle triggered states
				const orderForLabel = {
					...order,
					triggerCondition: ENUM_UTILS.match(
						order.triggerCondition,
						OrderTriggerCondition.TRIGGERED_ABOVE
					)
						? OrderTriggerCondition.ABOVE
						: OrderTriggerCondition.BELOW,
				};
				const orderType = getOrderLabelFromOrderDetails(orderForLabel);

				const baseSize = formatOrderSize(order.baseAssetAmount);
				const triggerPrice = NumLib.formatNum.toTradePrecision(
					order.triggerPrice.toNum()
				);

				notify({
					type: 'success',
					message: `${marketName} ${orderType} Triggered`,
					description: `Your ${baseSize} ${marketName} ${orderType} order with trigger price $${triggerPrice} was just triggered!`,
					action: {
						type: 'link',
						label: 'View market',
						href: `/trade/${marketName.toLowerCase()}`,
					},
					showUntilCancelled: true,
				});
			});

			// Cache current orders for next comparison
			previousOrdersRef.current = [...nonZeroOrders];
		},
		[]
	);

	useEffect(() => {
		if (!userKey || !user?.isSubscribed) return;

		const handler = () => {
			const nonZeroOrders = getAllNonZeroOrders(user);
			checkForTriggeredOrders(nonZeroOrders);
		};

		// Run immediately
		handler();

		user.eventEmitter.on('userAccountUpdate', handler);
		return () => {
			user.eventEmitter.removeListener('userAccountUpdate', handler);
		};
	}, [userKey, user, checkForTriggeredOrders]);
}
